import type {MantineColor} from '@mantine/core';
import {ActionIcon, Group, Menu, Tooltip} from '@mantine/core';
import {
  IconArrowRight,
  IconCopy,
  IconDotsVertical,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import type {ReactNode} from 'react';
import React from 'react';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';

/**
 * Composable action buttons for table rows — view/edit action icons plus an
 * overflow menu.
 * @registry Row action bar for tables: view/edit icons + overflow menu.
 *
 * Ported from `library-m/data-display/TableRowActions`. Prop surface and
 * compound API (`.View`, `.Edit`, `.Menu`, `.MenuItem`) kept identical.
 * Icon import switched from `library-m/feedback/icons/CommonIcons` to the
 * already-ported `design/atoms/icons/CommonIcons` (same exports).
 *
 * Usage:
 * ```tsx
 * <TableRowActions>
 *   <TableRowActions.View onClick={() => navigate(...)} />
 *   <TableRowActions.Edit onClick={() => navigate(...)} />
 *   <TableRowActions.Menu>
 *     <TableRowActions.MenuItem icon={<IconCopy />} onClick={handleClone}>
 *       Clone
 *     </TableRowActions.MenuItem>
 *     <TableRowActions.MenuItem color="red" onClick={handleDelete}>
 *       Delete
 *     </TableRowActions.MenuItem>
 *   </TableRowActions.Menu>
 * </TableRowActions>
 * ```
 */

// --- Sub-component Props ---

export interface TableRowActionsProps {
  children: ReactNode;
  /** Size of action icons */
  size?: 'sm' | 'md';
}

export interface TableRowActionsViewProps {
  onClick: () => void;
  tooltip?: string;
}

export interface TableRowActionsEditProps {
  onClick: () => void;
  tooltip?: string;
}

export interface TableRowActionsMenuProps {
  children: ReactNode;
  /** Dropdown width in px. Defaults to 160. Widen to keep long labels on one line. */
  width?: number;
}

export interface TableRowActionsMenuItemProps {
  children: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  color?: MantineColor;
  disabled?: boolean;
}

// --- Context for size ---

const TableRowActionsContext = React.createContext<{
  iconSize: number;
  actionIconSize: 'sm' | 'md';
}>({
  iconSize: IconSize.md,
  actionIconSize: 'sm',
});

// --- Sub-components ---

const TableRowActionsView = ({
  onClick,
  tooltip = 'View details',
}: TableRowActionsViewProps) => {
  const {iconSize, actionIconSize} = React.useContext(TableRowActionsContext);

  return (
    <Tooltip label={tooltip} position="top" withArrow>
      <ActionIcon
        variant="subtle"
        color="black"
        size={actionIconSize}
        onClick={onClick}>
        <IconArrowRight size={iconSize} />
      </ActionIcon>
    </Tooltip>
  );
};
TableRowActionsView.displayName = 'TableRowActions.View';

const TableRowActionsEdit = ({
  onClick,
  tooltip = 'Edit',
}: TableRowActionsEditProps) => {
  const {iconSize, actionIconSize} = React.useContext(TableRowActionsContext);

  return (
    <Tooltip label={tooltip} position="top" withArrow>
      <ActionIcon
        variant="subtle"
        color="gray"
        size={actionIconSize}
        onClick={onClick}>
        <IconPencil size={iconSize} />
      </ActionIcon>
    </Tooltip>
  );
};
TableRowActionsEdit.displayName = 'TableRowActions.Edit';

const TableRowActionsMenuItem = ({
  children,
  icon,
  onClick,
  color,
  disabled = false,
}: TableRowActionsMenuItemProps) => (
  <Menu.Item
    leftSection={icon}
    onClick={onClick}
    color={color}
    disabled={disabled}>
    {children}
  </Menu.Item>
);
TableRowActionsMenuItem.displayName = 'TableRowActions.MenuItem';

const TableRowActionsMenu = ({
  children,
  width = 160,
}: TableRowActionsMenuProps) => {
  const {iconSize, actionIconSize} = React.useContext(TableRowActionsContext);

  return (
    <Menu shadow="md" width={width} position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size={actionIconSize}>
          <IconDotsVertical size={iconSize} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>{children}</Menu.Dropdown>
    </Menu>
  );
};
TableRowActionsMenu.displayName = 'TableRowActions.Menu';

// --- Main Component ---

const TableRowActionsRoot = ({
  children,
  size = 'sm',
}: TableRowActionsProps) => {
  const iconSize = size === 'sm' ? IconSize.md : IconSize.lg;
  const actionIconSize = size === 'sm' ? 'sm' : 'md';

  return (
    <TableRowActionsContext.Provider value={{iconSize, actionIconSize}}>
      <Group gap={4} wrap="nowrap">
        {children}
      </Group>
    </TableRowActionsContext.Provider>
  );
};
TableRowActionsRoot.displayName = 'TableRowActions';

// --- Compound Component Export ---

export const TableRowActions = Object.assign(TableRowActionsRoot, {
  View: TableRowActionsView,
  Edit: TableRowActionsEdit,
  Menu: TableRowActionsMenu,
  MenuItem: TableRowActionsMenuItem,
});

// Also export individual components for flexibility
export {
  TableRowActionsEdit,
  TableRowActionsMenu,
  TableRowActionsMenuItem,
  TableRowActionsView,
};

// --- Convenience Components ---

/**
 * Simple view-only action for tables.
 * Use when you only need a "view details" action without composition.
 */
export const TableViewAction = ({
  onClick,
  tooltip = 'View details',
}: {
  onClick: () => void;
  tooltip?: string;
}) => (
  <Tooltip label={tooltip} position="top" withArrow>
    <ActionIcon variant="light" color="blue" size="sm" onClick={onClick}>
      <IconArrowRight size={IconSize.md} />
    </ActionIcon>
  </Tooltip>
);

// --- Common Menu Item Presets ---

export const CloneMenuItem = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <TableRowActionsMenuItem
    icon={<IconCopy size={IconSize.sm} />}
    onClick={onClick}
    disabled={disabled}>
    Clone
  </TableRowActionsMenuItem>
);

export const DeleteMenuItem = ({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) => (
  <TableRowActionsMenuItem
    icon={<IconTrash size={IconSize.sm} />}
    onClick={onClick}
    color="red"
    disabled={disabled}>
    Delete
  </TableRowActionsMenuItem>
);
